/**
 * Pixi vykresľovanie hracej plochy. Renderer nič nevie o pravidlách —
 * dostane hotový pohľad (`BoardView`) a hlási späť kliknutia.
 *
 * Vrstvy zdola nahor:
 *   obrázok dosky → spojnice → zvýraznenia → priestory → efekty (šípka útoku)
 *   → žetóny → plávajúce čísla (poškodenie, liečenie).
 *
 * Vzhľad podľa docs/design/5-hra.png: plátno je priehľadné nad bodkovanou
 * mriežkou (CSS), priestory aj žetóny sú zaoblené štvorce, spojnice plné čiary.
 */

import type { FighterId, GameMap, SpaceId } from '@unmatched/engine';
import { Application, Assets, Container, Graphics, Sprite, Text, Texture } from 'pixi.js';

export interface TokenView {
  id: FighterId;
  owner: number;
  kind: 'hero' | 'sidekick';
  name: string;
  hp: number;
  maxHp: number;
  spaceId: SpaceId;
  /** SVG ikona postavy; ak chýba alebo sa nenačíta, ostane písmeno. */
  icon?: string;
  /** Portrét z artu vlastnej karty (`/api/cards/:id/image`). Má prednosť pred ikonou. */
  portrait?: string;
}

export interface BoardView {
  tokens: TokenView[];
  selectedFighter: FighterId | null;
  /** Zóny, kam sa vybraná figúrka smie pohnúť. */
  reachable: Set<SpaceId>;
  /** Figúrky, na ktoré sa dá práve zaútočiť. */
  targets: Set<FighterId>;
  /** Ktoré figúrky smie hráč práve ovládať. */
  controllable: Set<FighterId>;
  /** Figúrky, ktoré smú zahrať kartu pod kurzorom (banner karty). */
  banner: Set<FighterId>;
  /** Hráč na rade — jeho figúrky dýchajú, aby bolo na doske vidieť, čí je ťah. */
  activeOwner: number | null;
  /** Prebiehajúci súboj: šípka od útočníka k obrancovi. */
  combat: { attacker: FighterId; defender: FighterId } | null;
  /** Žetóny hmly (Invisible Man). */
  fog?: { id: string; spaceId: SpaceId }[];
  /** Hmly, ktoré sa dajú práve vybrať (efekt „posuň hmlu"). */
  selectableFog?: Set<string>;
  selectedFog?: string | null;
}

/** Jednorazový efekt na doske — napr. „−3" nad zasiahnutou figúrkou. */
export interface BoardEffect {
  /** Unikátny kľúč; ten istý efekt sa neprehrá dvakrát. */
  key: string;
  fighterId: FighterId;
  text: string;
  tone: 'damage' | 'heal';
}

export interface BoardCallbacks {
  onSpaceClick(spaceId: SpaceId): void;
  onFighterClick(fighterId: FighterId): void;
}

/** Rezerva okolo mapy pri prispôsobení do okna, v jednotkách mapy. */
const TABLE_PAD = 46;

/** Zhodné s paletou v styles.css — --p0..--p3. */
export const PLAYER_COLORS = [0x3b82f6, 0xef4444, 0x22c55e, 0xa855f7];
const COLORS = {
  path: 0x3a4050,
  pathShadow: 0x05070b,
  node: 0x161a24,
  /** Primárna (jantárová) je v celom UI farba „toto sa dá urobiť". */
  reachable: 0xf59e0b,
  // Banner nie je výzva na klik, preto nie jantárová — neutrálna svetlá.
  banner: 0xe0e3f5,
  target: 0xef4444,
  label: 0x9aa3b8,
  damage: 0xff5a64,
  heal: 0x5fdc97,
};

/** Zaoblenie štvorca s polovicou strany `half`. */
function cornerOf(half: number): number {
  return Math.round(half * 0.28);
}

function hexToNumber(hex: string): number {
  return Number.parseInt(hex.replace('#', ''), 16);
}

interface TokenSprite {
  container: Container;
  hpText: Text;
  ring: Graphics;
  /** Dýchajúci prstenec hráča na ťahu — kreslí sa v tickeri. */
  pulse: Graphics;
  owner: number;
  radius: number;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
}

interface Floater {
  text: Text;
  age: number;
}

export class BoardRenderer {
  private readonly world = new Container();
  /** Podklad: naskenovaná doska, ak k mape existuje. Schéma sa kreslí naň. */
  private readonly boardLayer = new Container();
  private readonly edgesLayer = new Graphics();
  /**
   * Kresba schémy (štvorce, názvy), ktorú netreba, keď pod ňou leží obrázok
   * skutočnej dosky — ten má vlastné kruhy, farby zón aj čiary. Skryje sa, až
   * keď sa obrázok naozaj načíta; bez neho ostáva schéma.
   */
  private readonly schemaParts: Container[] = [];
  private readonly highlightLayer = new Graphics();
  private readonly spacesLayer = new Container();
  /** Opar hmly leží pod žetónmi — figúrka stojí V hmle. */
  private readonly fogLayer = new Graphics();
  /**
   * Obláčik hmly v rohu priestoru je NAD žetónmi — hrdina na hmle by ho inak
   * takmer celý prekryl a nebolo by vidieť, že v hmle stojí.
   */
  private readonly fogBadgeLayer = new Graphics();
  private readonly fxLayer = new Graphics();
  private readonly tokensLayer = new Container();
  private readonly floatLayer = new Container();
  private readonly tokens = new Map<FighterId, TokenSprite>();
  private readonly spaceById = new Map<SpaceId, { x: number; y: number; r: number }>();
  private readonly floaters: Floater[] = [];
  private readonly playedEffects = new Set<string>();

  private view: BoardView | null = null;
  private time = 0;
  private dragging = false;
  private lastPointer = { x: 0, y: 0 };
  private disposed = false;
  private observer: ResizeObserver | null = null;
  /** Držíme referenciu, aby sa dal callback pri rušení odobrať z tickera. */
  private readonly onTick = (ticker: { deltaMS: number }): void => this.tick(ticker.deltaMS);

  constructor(
    private readonly app: Application,
    private readonly map: GameMap,
    private readonly callbacks: BoardCallbacks,
  ) {
    this.world.addChild(
      this.boardLayer,
      this.edgesLayer,
      this.highlightLayer,
      this.spacesLayer,
      this.fogLayer,
      this.fxLayer,
      this.tokensLayer,
      this.fogBadgeLayer,
      this.floatLayer,
    );
    app.stage.addChild(this.world);
    // Vrstvy nad priestormi sú len kresba — kliky musia prepadnúť na priestor
    // pod nimi. Bez toho sa na priestor s hmlou nedalo postaviť.
    for (const layer of [this.fogLayer, this.fogBadgeLayer, this.fxLayer, this.floatLayer]) {
      layer.eventMode = 'none';
    }

    for (const space of map.spaces) {
      this.spaceById.set(space.id, { x: space.x, y: space.y, r: space.r });
    }

    void this.loadBackground();
    this.drawEdges();
    this.drawSpaces();
    this.fitToScreen();
    this.setupCamera();

    app.ticker.add(this.onTick);

    // Pixi si pri `resizeTo` prepočíta plátno až vo vlastnom listeneri, takže
    // pri odchytení window.resize by `app.screen` ešte držal staré rozmery.
    // ResizeObserver na hostiteľovi + explicitný `app.resize()` to poradie určí.
    this.observer = new ResizeObserver(() => {
      if (this.disposed) return;
      this.app.resize();
      this.fitToScreen();
      // Pixi po `resize()` ešte raz prepočíta plátno vo vlastnom snímku —
      // bez druhého prispôsobenia ostala doska pri zmenšení okna orezaná.
      requestAnimationFrame(() => {
        if (this.disposed) return;
        this.app.resize();
        this.fitToScreen();
      });
    });
    const host = app.canvas.parentElement;
    if (host) this.observer.observe(host);
  }

  // --- statická časť scény -------------------------------------------------

  /**
   * Spojenia sú plné čiary od okraja k okraju priestoru. V hre sa počítajú
   * kroky po hranách, nie pixely — čiara hovorí len „tadiaľto sa dá prejsť".
   */
  private drawEdges(): void {
    for (const [a, b] of this.map.edges) {
      const from = this.spaceById.get(a);
      const to = this.spaceById.get(b);
      if (!from || !to) continue;

      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const length = Math.hypot(dx, dy) || 1;
      const ux = dx / length;
      const uy = dy / length;

      // Čiara začína a končí na okraji priestoru, nie v jeho strede.
      const sx = from.x + ux * (from.r + 4);
      const sy = from.y + uy * (from.r + 4);
      const ex = to.x - ux * (to.r + 4);
      const ey = to.y - uy * (to.r + 4);

      // Tieň pod spojnicou, aby čiara nesplývala s mriežkou pod doskou.
      this.edgesLayer
        .moveTo(sx, sy)
        .lineTo(ex, ey)
        .stroke({ width: 10, color: COLORS.pathShadow, alpha: 0.5, cap: 'round' })
        .moveTo(sx, sy)
        .lineTo(ex, ey)
        .stroke({ width: 5, color: COLORS.path, alpha: 0.95, cap: 'round' });
    }
  }

  /**
   * Priestor je zaoblený štvorec: tmavý stred s nádychom zóny a obrys vo farbe
   * zóny. Farba nesie pravidlo, nie dekoráciu — ranged útok zasiahne
   * kohokoľvek v rovnakej zóne. Priestor v dvoch zónach má obrys **rozdelený
   * na dve polovice** (ľavá / pravá), rovnako ako delený prstenec na doske.
   */
  private drawSpaces(): void {
    const zoneColor = new Map(this.map.zones.map((zone) => [zone.id, hexToNumber(zone.color)]));

    for (const space of this.map.spaces) {
      const node = new Container();
      node.x = space.x;
      node.y = space.y;
      node.eventMode = 'static';
      node.cursor = 'pointer';
      const half = space.r * 0.9;
      const corner = cornerOf(half);
      node.hitArea = { contains: (x: number, y: number) => Math.abs(x) <= half && Math.abs(y) <= half };
      node.on('pointertap', () => this.callbacks.onSpaceClick(space.id));

      const colors = space.zones.map((zone) => zoneColor.get(zone) ?? 0x3a4358);
      const primary = colors[0] ?? 0x3a4358;

      // Žiara zóny okolo štvorca — zóny sú čitateľné aj z diaľky.
      const glow = new Graphics()
        .roundRect(-half - 10, -half - 10, (half + 10) * 2, (half + 10) * 2, corner + 8)
        .fill({ color: primary, alpha: 0.07 })
        .roundRect(-half - 5, -half - 5, (half + 5) * 2, (half + 5) * 2, corner + 4)
        .fill({ color: primary, alpha: 0.08 });
      const body = new Graphics()
        .roundRect(-half, -half, half * 2, half * 2, corner)
        .fill({ color: COLORS.node, alpha: 0.96 })
        .roundRect(-half, -half, half * 2, half * 2, corner)
        .fill({ color: primary, alpha: 0.13 });

      const frame = new Graphics();
      if (colors.length > 1) {
        // Dve zóny: ľavá polovica obrysu jednou farbou, pravá druhou.
        const second = colors[1]!;
        const leftHalf = new Graphics()
          .roundRect(-half, -half, half * 2, half * 2, corner)
          .stroke({ width: 4, color: primary, alpha: 0.95 });
        const leftMask = new Graphics().rect(-half - 4, -half - 4, half + 4, half * 2 + 8).fill(0xffffff);
        leftHalf.mask = leftMask;
        const rightHalf = new Graphics()
          .roundRect(-half, -half, half * 2, half * 2, corner)
          .stroke({ width: 4, color: second, alpha: 0.95 });
        const rightMask = new Graphics().rect(0, -half - 4, half + 4, half * 2 + 8).fill(0xffffff);
        rightHalf.mask = rightMask;
        frame.addChild(leftMask, leftHalf, rightMask, rightHalf);
        // Druhá zóna aj v nádychu výplne, nech je delenie vidieť aj bez obrysu.
        body.roundRect(0, -half, half, half * 2, corner).fill({ color: second, alpha: 0.1 });
      } else {
        frame.roundRect(-half, -half, half * 2, half * 2, corner).stroke({ width: 3, color: primary, alpha: 0.85 });
      }

      node.addChild(glow, body, frame);
      this.schemaParts.push(glow, body, frame);

      if (space.start !== undefined) {
        // Štartový priestor: číslovaný štítok na hornej hrane.
        const badge = new Graphics()
          .roundRect(-13, -half - 12, 26, 22, 5)
          .fill({ color: 0xe0e3f5 })
          .stroke({ width: 2, color: 0x0b0f19 });
        const number = new Text({
          text: `${space.start}`,
          style: { fill: 0x0b0f19, fontSize: 14, fontWeight: 'bold', fontFamily: 'Space Grotesk, system-ui, sans-serif' },
        });
        number.anchor.set(0.5);
        number.y = -half - 1;
        node.addChild(badge, number);
        this.schemaParts.push(badge, number);
      }

      const label = new Text({
        text: space.label.toUpperCase(),
        style: {
          fill: COLORS.label,
          fontSize: 15,
          fontWeight: '600',
          fontFamily: 'Space Grotesk, system-ui, sans-serif',
          letterSpacing: 1.2,
        },
      });
      label.anchor.set(0.5, 0);
      // Názov ide POD štvorec — vnútri by ho prekryl žetón.
      const plateW = label.width + 14;
      const plate = new Graphics()
        .roundRect(-plateW / 2, half + 7, plateW, label.height + 4, 4)
        .fill({ color: 0x0b0f19, alpha: 0.85 });
      label.y = half + 9;
      node.addChild(plate, label);
      this.schemaParts.push(plate, label);

      // Tajná chodba (Baskerville Manor): krúžok s bodkou ako kľúčová dierka.
      if ((this.map.passages ?? []).some((group) => group.includes(space.id))) {
        const keyhole = new Graphics()
          .circle(half - 8, half - 8, 11)
          .fill({ color: 0xe0e3f5 })
          .stroke({ width: 2, color: 0x0b0f19 })
          .circle(half - 8, half - 10, 3)
          .fill({ color: 0x0b0f19 })
          .rect(half - 9, half - 9, 2, 6)
          .fill({ color: 0x0b0f19 });
        node.addChild(keyhole);
        this.schemaParts.push(keyhole);
      }

      this.spacesLayer.addChild(node);
    }
  }

  /**
   * Obrázok dosky pod schému. Súradnice priestorov sú v sústave mapy
   * (0..w, 0..h), takže obrázok stačí natiahnuť na presne tie rozmery —
   * orezaný sken sa tak zhoduje s obkreslenými poľami bez ďalšieho počítania.
   *
   * Načítava sa asynchrónne a mapa funguje aj bez neho: keď obrázok nedorazí,
   * zostane schéma, ako bola doteraz.
   */
  private async loadBackground(): Promise<void> {
    if (!this.map.image) return;
    try {
      // `loadParser` je nutný: URL podkladu nemá príponu (`/api/boards/x/image`)
      // a Pixi by z nej sám nespoznal, že ide o obrázok.
      const texture = await Assets.load<Texture>({
        src: this.map.image,
        loadParser: 'loadTextures',
      });
      if (this.disposed) return;
      const sprite = new Sprite(texture);
      sprite.width = this.map.w;
      sprite.height = this.map.h;
      this.boardLayer.addChild(sprite);
      // Doska má vlastné kruhy, zóny aj čiary — schéma by ich len prekryla.
      this.edgesLayer.visible = false;
      for (const part of this.schemaParts) part.visible = false;
    } catch {
      // Chýbajúci podklad nie je dôvod nechať hráča bez dosky.
    }
  }

  // --- kamera --------------------------------------------------------------

  private fitToScreen = (): void => {
    if (this.disposed) return;
    const { width, height } = this.app.screen;
    // Do okna sa vojde STÔL (mapa + jeho okraj), nie holá mapa s veľkou rezervou —
    // inak doska zaberie sotva polovicu miesta, ktoré jej patrí.
    const margin = 18;
    const w = this.map.w + TABLE_PAD * 2;
    const h = this.map.h + TABLE_PAD * 2;
    const scale = Math.min((width - margin * 2) / w, (height - margin * 2) / h);
    this.world.scale.set(scale);
    this.world.x = (width - this.map.w * scale) / 2;
    this.world.y = (height - this.map.h * scale) / 2;
  };

  private setupCamera(): void {
    const stage = this.app.stage;
    stage.eventMode = 'static';
    stage.hitArea = { contains: () => true };

    stage.on('pointerdown', (event) => {
      this.dragging = true;
      this.lastPointer = { x: event.global.x, y: event.global.y };
    });
    stage.on('pointerup', () => (this.dragging = false));
    stage.on('pointerupoutside', () => (this.dragging = false));
    stage.on('globalpointermove', (event) => {
      if (!this.dragging) return;
      this.world.x += event.global.x - this.lastPointer.x;
      this.world.y += event.global.y - this.lastPointer.y;
      this.lastPointer = { x: event.global.x, y: event.global.y };
    });

    this.app.canvas.addEventListener('wheel', this.onWheel, { passive: false });
  }

  private onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    const factor = event.deltaY < 0 ? 1.1 : 1 / 1.1;
    const next = Math.min(3, Math.max(0.3, this.world.scale.x * factor));
    const rect = this.app.canvas.getBoundingClientRect();
    const px = event.clientX - rect.left;
    const py = event.clientY - rect.top;
    // Zoom smerom ku kurzoru: bod pod myšou zostane na mieste.
    this.world.x = px - ((px - this.world.x) / this.world.scale.x) * next;
    this.world.y = py - ((py - this.world.y) / this.world.scale.y) * next;
    this.world.scale.set(next);
  };

  // --- dynamická časť ------------------------------------------------------

  update(view: BoardView): void {
    this.view = view;
    this.drawHighlights(view);
    this.drawFog(view);
    this.syncTokens(view);
  }

  /**
   * Hmla ako oblak v rohu priestoru — kresli sa pod figúrky, lebo figúrka
   * stojí v hmle. Viac hmiel na jednom priestore sa rozloží vedľa seba.
   * Vybrateľná hmla má jantárový obrys (dá sa na ňu kliknúť cez priestor).
   */
  private drawFog(view: BoardView): void {
    const haze = this.fogLayer;
    const badge = this.fogBadgeLayer;
    haze.clear();
    badge.clear();
    const perSpace = new Map<SpaceId, number>();
    for (const fog of view.fog ?? []) {
      const space = this.spaceById.get(fog.spaceId);
      if (!space) continue;
      const index = perSpace.get(fog.spaceId) ?? 0;
      perSpace.set(fog.spaceId, index + 1);
      const half = space.r * 0.9;
      const corner = Math.round(half * 0.28);

      // Opar cez celý priestor + svetlý prerušovaný lem — viditeľný aj okolo
      // figúrky, ktorá stred priestoru zakryje.
      if (index === 0) {
        haze.roundRect(space.x - half, space.y - half, half * 2, half * 2, corner).fill({
          color: 0xc9d3e6,
          alpha: 0.14,
        });
        haze
          .roundRect(space.x - half - 7, space.y - half - 7, (half + 7) * 2, (half + 7) * 2, corner + 5)
          .stroke({ width: 3, color: 0xe6ecf7, alpha: 0.55 });
      }

      // Obláčik na ľavom dolnom rohu, čiastočne mimo priestoru — nad žetónom.
      const cx = space.x - half + 6 + index * 30;
      const cy = space.y + half - 2;
      const puffs = [
        [-14, 4, 13],
        [0, -6, 16],
        [15, 4, 13],
        [0, 8, 14],
      ] as const;
      for (const [dx, dy, r] of puffs) badge.circle(cx + dx, cy + dy, r + 2.5).fill({ color: 0x0b0f19, alpha: 0.9 });
      for (const [dx, dy, r] of puffs) badge.circle(cx + dx, cy + dy, r).fill({ color: 0xeef2f9 });

      const selectable = view.selectableFog?.has(fog.id);
      const selected = view.selectedFog === fog.id;
      if (selectable || selected) {
        badge.roundRect(cx - 33, cy - 27, 66, 50, 18).stroke({
          width: selected ? 4 : 3,
          color: COLORS.reachable,
          alpha: selected ? 1 : 0.85,
        });
      }
    }
  }

  /**
   * Plávajúce číslo nad figúrkou. Efekt s rovnakým kľúčom sa prehrá len raz —
   * React ich posiela deklaratívne a pri každom prekreslení by sa opakovali.
   */
  play(effects: readonly BoardEffect[]): void {
    for (const effect of effects) {
      if (this.playedEffects.has(effect.key)) continue;
      this.playedEffects.add(effect.key);
      const sprite = this.tokens.get(effect.fighterId);
      if (!sprite) continue;
      const text = new Text({
        text: effect.text,
        style: {
          fill: effect.tone === 'heal' ? COLORS.heal : COLORS.damage,
          fontSize: 38,
          fontWeight: 'bold',
          fontFamily: 'Space Grotesk, system-ui, sans-serif',
          stroke: { color: 0x05070b, width: 6, join: 'round' },
        },
      });
      text.anchor.set(0.5);
      text.x = sprite.x;
      text.y = sprite.y - sprite.radius - 10;
      this.floatLayer.addChild(text);
      this.floaters.push({ text, age: 0 });
    }
  }

  private drawHighlights(view: BoardView): void {
    this.highlightLayer.clear();
    for (const spaceId of view.reachable) {
      const space = this.spaceById.get(spaceId);
      if (!space) continue;
      const half = space.r * 0.9 + 8;
      this.highlightLayer
        .roundRect(space.x - half, space.y - half, half * 2, half * 2, cornerOf(half))
        .fill({ color: COLORS.reachable, alpha: 0.14 })
        .stroke({ width: 4, color: COLORS.reachable, alpha: 0.95 });
    }
  }

  private syncTokens(view: BoardView): void {
    const seen = new Set<FighterId>();

    view.tokens.forEach((token) => {
      seen.add(token.id);
      const space = this.spaceById.get(token.spaceId);
      if (!space) return;

      const siblings = view.tokens.filter((t) => t.spaceId === token.spaceId);
      const slot = siblings.findIndex((t) => t.id === token.id);
      const offset = tokenOffset(slot, siblings.length, space.r);

      let sprite = this.tokens.get(token.id);
      if (!sprite) {
        sprite = this.createToken(token);
        this.tokens.set(token.id, sprite);
        sprite.x = space.x + offset.x;
        sprite.y = space.y + offset.y;
        sprite.container.x = sprite.x;
        sprite.container.y = sprite.y;
      }
      sprite.targetX = space.x + offset.x;
      sprite.targetY = space.y + offset.y;
      sprite.hpText.text = `${token.hp}`;

      const selected = view.selectedFighter === token.id;
      const targeted = view.targets.has(token.id);
      sprite.ring.clear();
      // Banner kreslíme ďalej od žetónu, aby sa nebil s výberom ani s cieľom.
      const bannered = view.banner.has(token.id);
      if (bannered) {
        square(sprite.ring, sprite.radius + 11).stroke({ width: 4, color: COLORS.banner, alpha: 0.95 });
        square(sprite.ring, sprite.radius + 16).stroke({ width: 2, color: COLORS.banner, alpha: 0.35 });
      }
      if (selected || targeted) {
        square(sprite.ring, sprite.radius + 7).stroke({
          width: 3.5,
          color: targeted ? COLORS.target : COLORS.reachable,
          alpha: 0.95,
        });
      }
      // Kým je karta pod kurzorom, ostatné figúrky ustúpia — nech je hneď vidieť,
      // ktoré ju smú zahrať.
      const shadowed = view.banner.size > 0 && !bannered;
      sprite.container.alpha = shadowed
        ? 0.3
        : view.controllable.has(token.id) || targeted || bannered
          ? 1
          : 0.9;
    });

    for (const [id, sprite] of this.tokens) {
      if (seen.has(id)) continue;
      sprite.container.destroy({ children: true });
      this.tokens.delete(id);
    }
  }

  private createToken(token: TokenView): TokenSprite {
    const container = new Container();
    container.eventMode = 'static';
    container.cursor = 'pointer';
    const radius = tokenRadius(token);
    container.hitArea = {
      contains: (x: number, y: number) => Math.abs(x) <= radius && Math.abs(y) <= radius,
    };
    container.on('pointertap', (event) => {
      event.stopPropagation();
      this.callbacks.onFighterClick(token.id);
    });

    const seat = PLAYER_COLORS[token.owner] ?? 0xaaaaaa;
    const pulse = new Graphics();
    const ring = new Graphics();
    // Žetón je portrét v zaoblenom štvorci so žiarou vo farbe hráča —
    // figúrka stojí NA doske, nie je do nej nakreslená.
    const shadow = square(new Graphics(), radius + 3, 0, 5).fill({ color: 0x000000, alpha: 0.5 });
    const glow = square(new Graphics(), radius + 7).fill({ color: seat, alpha: 0.16 });
    const body = square(new Graphics(), radius).fill({ color: 0x161a24 });
    const frame = square(new Graphics(), radius)
      .stroke({ width: 3.5, color: seat })
      .roundRect(-radius + 2.5, -radius + 2.5, radius * 2 - 5, radius * 2 - 5, cornerOf(radius) - 2)
      .stroke({ width: 1, color: 0x000000, alpha: 0.5 });

    const initial = new Text({
      text: token.name.slice(0, 1).toUpperCase(),
      style: { fill: seat, fontSize: radius, fontWeight: 'bold', fontFamily: 'Space Grotesk, system-ui, sans-serif' },
    });
    initial.anchor.set(0.5);
    initial.y = -4;

    // Zdravie ako štítok na spodnej hrane — farba hráča, číslo ostro biele.
    const hpBg = new Graphics()
      .roundRect(-15, radius - 11, 30, 20, 4)
      .fill({ color: seat })
      .stroke({ width: 2, color: 0x0b0f19 });
    const hpText = new Text({
      text: `${token.hp}`,
      style: { fill: 0xffffff, fontSize: 15, fontWeight: 'bold', fontFamily: 'Space Grotesk, system-ui, sans-serif' },
    });
    hpText.anchor.set(0.5);
    hpText.y = radius - 1;

    container.addChild(pulse, shadow, glow, ring, body, initial, frame, hpBg, hpText);
    this.tokensLayer.addChild(container);

    // Portrét má prednosť; keď nie je alebo zlyhá, skúsi sa ikona, potom písmeno.
    if (token.portrait) {
      void this.attachPortrait(container, initial, token.portrait, radius, token.icon, seat);
    } else if (token.icon) {
      void this.attachIcon(container, initial, token.icon, radius, seat);
    }

    return {
      container,
      hpText,
      ring,
      pulse,
      owner: token.owner,
      radius,
      x: 0,
      y: 0,
      targetX: 0,
      targetY: 0,
    };
  }

  /**
   * Portrét z artu karty, orezaný do štvorca. Postava je na kartách Unmatched
   * v hornej tretine, preto kotva na ~32 % výšky obrázka.
   */
  private async attachPortrait(
    container: Container,
    placeholder: Text,
    url: string,
    radius: number,
    fallbackIcon: string | undefined,
    seat: number,
  ): Promise<void> {
    try {
      const texture = await Assets.load<Texture>({ src: url, loadParser: 'loadTextures' });
      if (this.disposed || container.destroyed) return;
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.58, 0.3);
      const width = radius * 2.35;
      sprite.width = width;
      sprite.height = width * (texture.height / texture.width || 1.4);
      const mask = square(new Graphics(), radius - 2).fill({ color: 0xffffff });
      sprite.mask = mask;
      const index = container.getChildIndex(placeholder);
      container.addChildAt(mask, index);
      container.addChildAt(sprite, index);
      placeholder.visible = false;
    } catch {
      if (fallbackIcon) void this.attachIcon(container, placeholder, fallbackIcon, radius, seat);
    }
  }

  private async attachIcon(
    container: Container,
    placeholder: Text,
    icon: string,
    radius: number,
    seat: number,
  ): Promise<void> {
    try {
      const texture: Texture = await Assets.load(icon);
      if (this.disposed || container.destroyed) return;
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      const size = radius * 1.35;
      sprite.width = size;
      sprite.height = size;
      // Kresba je biela → tint ju zafarbí farbou hráča.
      sprite.tint = seat;
      container.addChildAt(sprite, container.getChildIndex(placeholder));
      placeholder.visible = false;
    } catch {
      // ikona sa nenačítala — zostáva písmeno
    }
  }

  /**
   * Šípka útoku od útočníka k obrancovi. Kreslí sa každý snímok, lebo žetóny
   * sa ešte môžu dosúvať a čiarky po šípke bežia — útok je udalosť, nie stav.
   */
  private drawCombat(): void {
    this.fxLayer.clear();
    const combat = this.view?.combat;
    if (!combat) return;
    const from = this.tokens.get(combat.attacker);
    const to = this.tokens.get(combat.defender);
    if (!from || !to) return;

    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy) || 1;
    const ux = dx / length;
    const uy = dy / length;
    const sx = from.x + ux * (from.radius + 8);
    const sy = from.y + uy * (from.radius + 8);
    const ex = to.x - ux * (to.radius + 14);
    const ey = to.y - uy * (to.radius + 14);
    const span = Math.hypot(ex - sx, ey - sy);

    // Bežiace čiarky: posun podľa času, aby šípka „tiekla" k cieľu.
    const dash = 16;
    const gap = 10;
    const phase = (this.time / 25) % (dash + gap);
    for (let d = -dash + phase; d < span; d += dash + gap) {
      const a = Math.max(0, d);
      const b = Math.min(span, d + dash);
      if (b <= a) continue;
      this.fxLayer
        .moveTo(sx + ux * a, sy + uy * a)
        .lineTo(sx + ux * b, sy + uy * b)
        .stroke({ width: 6, color: COLORS.target, alpha: 0.95, cap: 'round' });
    }
    // Hrot šípky.
    const head = 20;
    this.fxLayer
      .poly([
        ex + ux * 6,
        ey + uy * 6,
        ex - ux * head - uy * head * 0.6,
        ey - uy * head + ux * head * 0.6,
        ex - ux * head + uy * head * 0.6,
        ey - uy * head - ux * head * 0.6,
      ])
      .fill({ color: COLORS.target });

    // Obranca je pod paľbou — červený pulz okolo neho.
    const beat = 0.5 + 0.5 * Math.sin(this.time / 140);
    square(this.fxLayer, to.radius + 10 + beat * 6, 0, 0, to.x, to.y).stroke({
      width: 3,
      color: COLORS.target,
      alpha: 0.35 + beat * 0.5,
    });
  }

  /** Plynulý presun žetónov, dýchanie hráča na ťahu a plávajúce čísla. */
  private tick(deltaMS: number): void {
    if (this.disposed) return;
    this.time += deltaMS;
    const active = this.view?.activeOwner ?? null;
    const breath = 0.5 + 0.5 * Math.sin(this.time / 420);

    for (const sprite of this.tokens.values()) {
      const dx = sprite.targetX - sprite.x;
      const dy = sprite.targetY - sprite.y;
      if (Math.abs(dx) < 0.4 && Math.abs(dy) < 0.4) {
        sprite.x = sprite.targetX;
        sprite.y = sprite.targetY;
      } else {
        sprite.x += dx * 0.18;
        sprite.y += dy * 0.18;
      }
      sprite.container.x = sprite.x;
      sprite.container.y = sprite.y;

      // Čí je ťah, vidno aj na doske: figúrky hráča na rade jemne dýchajú.
      sprite.pulse.clear();
      if (active !== null && sprite.owner === active) {
        const seat = PLAYER_COLORS[sprite.owner] ?? 0xffffff;
        square(sprite.pulse, sprite.radius + 6 + breath * 6).fill({
          color: seat,
          alpha: 0.12 + breath * 0.14,
        });
      }
    }

    this.drawCombat();

    for (let i = this.floaters.length - 1; i >= 0; i--) {
      const floater = this.floaters[i]!;
      floater.age += deltaMS;
      const t = floater.age / 1500;
      floater.text.y -= deltaMS * 0.035;
      floater.text.scale.set(t < 0.15 ? 0.6 + t * 3.2 : 1.08 - Math.min(t, 1) * 0.08);
      floater.text.alpha = t < 0.7 ? 1 : Math.max(0, 1 - (t - 0.7) / 0.3);
      if (t >= 1) {
        floater.text.destroy();
        this.floaters.splice(i, 1);
      }
    }
  }

  destroy(): void {
    // Poradie je dôležité: najprv odpojiť všetko, čo by mohlo bežať nad
    // zničenou scénou (ticker, resize, koliesko), až potom rušiť objekty.
    this.disposed = true;
    this.observer?.disconnect();
    this.app.ticker.remove(this.onTick);
    this.app.canvas.removeEventListener('wheel', this.onWheel);
    this.tokens.clear();
    this.floaters.length = 0;
    this.world.destroy({ children: true });
  }
}

/** Zaoblený štvorec s polovicou strany `half` a stredom v (cx + dx, cy + dy). */
function square(g: Graphics, half: number, dx = 0, dy = 0, cx = 0, cy = 0): Graphics {
  return g.roundRect(cx + dx - half, cy + dy - half, half * 2, half * 2, cornerOf(half));
}

function tokenRadius(token: TokenView): number {
  // Žetón takmer vyplní priestor, ako v mocku — portrét musí byť čitateľný.
  return token.kind === 'hero' ? 40 : 29;
}

/** Viac figúrok v jednej zóne sa rozloží po kružnici. */
function tokenOffset(index: number, total: number, spaceRadius: number): { x: number; y: number } {
  if (total <= 1) return { x: 0, y: 0 };
  const spread = Math.min(spaceRadius * 0.55, 34);
  const angle = (Math.PI * 2 * index) / total - Math.PI / 2;
  return { x: Math.cos(angle) * spread, y: Math.sin(angle) * spread };
}
